import {
  type PayloadAction,
  createSelector,
  createSlice,
} from '@reduxjs/toolkit';
import type { ActionSettings } from 'components/Advanced';
import { keyBy } from 'lodash';
import type { TAppThunk, TRootState } from 'slices/store';
import { createAppAsyncThunk } from 'slices/utils';
import type {
  IDefaultAPIResult,
  TMoveReservationsInfo,
  TMoveToTrashResponse,
  TReservationTemplatesState,
} from 'types/state.interface';
import { checkSavedDisabled } from 'utils/isSavedDisabled';
import {
  finishedLoadingFailure,
  finishedLoadingSuccess,
  isLoadingRequest,
} from 'utils/sliceHelpers';
import {
  EOptionalRelation,
  ETemplateType,
  type TMoveReservationsRequest,
  type TReservationTemplate,
  createEmptyReservationTemplate,
} from '@timeedit/types';
import api from '../services/api.service';

export const initialState: TReservationTemplatesState = {
  loading: false,
  hasErrors: false,
  firstSelectedReservationTemplate: null,
  secondSelectedReservationTemplate: null,
  allReservationTemplates: [],
  reservationTemplatesForOtherPages: [],
  map: {},
  advancedSettings: {},
  movedReservationsInfo: {},
  movedToTrashResponse: {},
  optionalRelatedTypes: [],
};

// Slice
const slice = createSlice({
  name: 'reservationTemplates',
  initialState,
  reducers: {
    fetchReservationTemplatesRequest: (state) => {
      isLoadingRequest(state);
    },
    fetchReservationTemplatesSuccess: (
      state,
      { payload }: PayloadAction<IDefaultAPIResult<TReservationTemplate>>
    ) => {
      state.allReservationTemplates = payload.results;
      state.map = keyBy(payload.results, 'id');
      state.optionalRelatedTypes = payload.results.flatMap(
        (template) =>
          template.types
            ?.filter(
              (type) => type.optionalRelation === EOptionalRelation.OPTIONAL
            )
            .map((t) => t.type as number) || []
      );

      finishedLoadingSuccess(state);
    },
    fetchReservationTemplatesForOthersSuccess: (
      state,
      { payload }: PayloadAction<IDefaultAPIResult<TReservationTemplate>>
    ) => {
      state.reservationTemplatesForOtherPages = payload.results;
      state.map = keyBy(payload.results, 'id');
    },
    fetchAdvancedSettingsReservationTemplatesSuccess: (
      state,
      {
        payload,
      }: PayloadAction<{ templateId: number; results: ActionSettings[] }>
    ) => {
      state.advancedSettings[payload.templateId] = payload.results;
      finishedLoadingSuccess(state);
    },
    fetchReservationTemplatesFailure: (state) => {
      finishedLoadingFailure(state);
    },
    createReservationTemplateRequest: (state) => {
      isLoadingRequest(state);
    },
    createReservationTemplateSuccess: (state) => {
      state.firstSelectedReservationTemplate = undefined;
      finishedLoadingSuccess(state);
    },
    createUnsavedReservationTemplateSuccess: (
      state,
      { payload }: PayloadAction<TReservationTemplate>
    ) => {
      if (state.firstSelectedReservationTemplate?.id === 0) return;
      state.allReservationTemplates.unshift(payload);
      state.map[0] = payload;
      state.firstSelectedReservationTemplate = payload;
    },
    createReservationTemplateFailure: (state) => {
      finishedLoadingFailure(state);
    },
    setReservationTemplate: (
      state,
      {
        payload: { isFirstTemplate, template },
      }: PayloadAction<{
        isFirstTemplate: boolean;
        template?: TReservationTemplate;
      }>
    ) => {
      if (isFirstTemplate) {
        state.firstSelectedReservationTemplate = template;
      } else {
        state.secondSelectedReservationTemplate = template;
      }
      if (template === undefined) {
        state.allReservationTemplates = state.allReservationTemplates.filter(
          (template: TReservationTemplate) => template.id !== 0
        );
        if (state.map[0]) state.map[0] = undefined;
      }
      state.movedReservationsInfo = null;
      state.movedToTrashResponse = null;
    },
    deleteReservationTemplateRequest: (state) => {
      isLoadingRequest(state);
    },
    deleteRowReservationTemplateSuccess: (
      state,
      {
        payload,
      }: PayloadAction<{
        reservationTemplate: Pick<TReservationTemplate, 'id'>;
      }>
    ) => {
      state.allReservationTemplates = state.allReservationTemplates.filter(
        (template: TReservationTemplate) =>
          template.id !== payload.reservationTemplate.id
      );
      state.map[payload.reservationTemplate.id] = undefined;
      if (
        payload.reservationTemplate.id ===
        state.firstSelectedReservationTemplate?.id
      ) {
        state.firstSelectedReservationTemplate = undefined;
      }
      if (
        payload.reservationTemplate.id ===
        state.secondSelectedReservationTemplate?.id
      ) {
        state.secondSelectedReservationTemplate = undefined;
      }
      finishedLoadingSuccess(state);
    },
    deleteReservationTemplateFailure: (state) => {
      finishedLoadingFailure(state);
    },
    updateReservationTemplateRequest: (state) => {
      isLoadingRequest(state);
    },
    updateReservationTemplateSuccess: (
      state,
      { payload }: PayloadAction<TReservationTemplate>
    ) => {
      const newReservationTemplates = [...state.allReservationTemplates];
      const index = state.allReservationTemplates.findIndex(
        (template: TReservationTemplate) => template.id === payload.id
      );
      newReservationTemplates[index] = payload;
      state.allReservationTemplates = newReservationTemplates;
      if (payload.id === state.firstSelectedReservationTemplate?.id) {
        state.firstSelectedReservationTemplate = undefined;
      }
      if (payload.id === state.secondSelectedReservationTemplate?.id) {
        state.secondSelectedReservationTemplate = undefined;
      }

      state.map[payload.id] = payload;
      finishedLoadingSuccess(state);
    },
    moveReservationsSuccess: (
      state,
      { payload }: PayloadAction<TMoveReservationsInfo>
    ) => {
      state.movedReservationsInfo = payload;
      finishedLoadingSuccess(state);
    },
    moveToTrashSuccess: (
      state,
      { payload }: PayloadAction<TMoveToTrashResponse>
    ) => {
      state.movedToTrashResponse = payload;
      finishedLoadingSuccess(state);
    },
    updateReservationTemplateFailure: (state) => {
      finishedLoadingFailure(state);
    },
  },
});

export default slice.reducer;

// Selectors
export const reservationTemplatesLoading = (state: TRootState) =>
  state.reservationTemplates.loading;

export const reservationTemplatesSelector = (state: TRootState) =>
  state.reservationTemplates.allReservationTemplates;

export const reservationTemplatesForOtherPagesSelector = (state: TRootState) =>
  state.reservationTemplates.reservationTemplatesForOtherPages;

export const firstSelectedReservationTemplateSelector = (state: TRootState) => {
  return state.reservationTemplates.firstSelectedReservationTemplate;
};

export const secondSelectedReservationTemplateSelector = (state: TRootState) =>
  state.reservationTemplates.secondSelectedReservationTemplate;

const EMPTY_ADVANCED_SETTINGS: ActionSettings[] = [];
export const advancedReservationTemplatesSelector =
  (template?: TReservationTemplate) => (state: TRootState) =>
    (template && state.reservationTemplates.advancedSettings[template.id]) ||
    EMPTY_ADVANCED_SETTINGS;

export const movedReservationsInfoSelector = (state: TRootState) =>
  state.reservationTemplates.movedReservationsInfo;

export const movedToTrashResponseSelector = (state: TRootState) =>
  state.reservationTemplates.movedToTrashResponse;

export const selectedReservationTemplateMap = (state: TRootState) =>
  state.reservationTemplates.map;

export const selectedReservationTemplateByResModeId =
  (resModeId: number) => (state: TRootState) =>
    state.reservationTemplates.allReservationTemplates.filter((temp) =>
      temp.reservationModes?.includes(resModeId)
    );

export const availabilityReservationTemplatesSelector = (state: TRootState) =>
  state.reservationTemplates.allReservationTemplates.filter(
    (template) => template.templateType === ETemplateType.AVAILABILITY
  );

export const getListIdSelected = createSelector(
  [
    (state: TRootState) =>
      state.reservationTemplates.firstSelectedReservationTemplate,
    (state: TRootState) =>
      state.reservationTemplates.secondSelectedReservationTemplate,
  ],
  (firstSelected, secondSelected) => {
    const result: number[] = [];
    if (firstSelected) result.push(firstSelected.id);
    if (secondSelected) result.push(secondSelected.id);
    return result;
  }
);

export const selectOptionalRelatedTypes = (state: TRootState) =>
  state.reservationTemplates.optionalRelatedTypes;

// Actions
export const {
  fetchReservationTemplatesRequest,
  fetchReservationTemplatesSuccess,
  fetchReservationTemplatesForOthersSuccess,
  fetchAdvancedSettingsReservationTemplatesSuccess,
  fetchReservationTemplatesFailure,
  createReservationTemplateRequest,
  createReservationTemplateSuccess,
  createUnsavedReservationTemplateSuccess,
  createReservationTemplateFailure,
  setReservationTemplate,
  deleteReservationTemplateRequest,
  deleteRowReservationTemplateSuccess,
  deleteReservationTemplateFailure,
  updateReservationTemplateRequest,
  updateReservationTemplateSuccess,
  updateReservationTemplateFailure,
  moveReservationsSuccess,
  moveToTrashSuccess,
} = slice.actions;

type TFetchReservationTemplatesPayload = {
  templateType?: string;
  reservationMode?: string;
  active?: string;
  searchText?: string;
};

export const fetchReservationTemplates = createAppAsyncThunk<
  void,
  void | TFetchReservationTemplatesPayload
>('reservationTemplates/fetch', async (payload = {}, { dispatch }) => {
  const {
    templateType = 'ALL',
    reservationMode,
    active = 'ALL',
    searchText,
  } = payload || {};

  try {
    dispatch(fetchReservationTemplatesRequest());

    const params: TFetchReservationTemplatesPayload = {
      ...(templateType && { templateType }),
      ...(reservationMode && { reservationMode }),
      ...(active && { active }),
      ...(searchText && { searchText }),
    };

    const reservationTemplates = await api.get({
      endpoint: `/reservation-templates?${new URLSearchParams(params)}`,
    });

    dispatch(fetchReservationTemplatesSuccess(reservationTemplates));
  } catch (e) {
    dispatch(fetchReservationTemplatesFailure());
    return console.error(e);
  }
});

export const fetchReservationTemplatesForOtherPages = createAppAsyncThunk(
  'reservationTemplates/fetchForOtherPages',
  async (_, { dispatch }) => {
    try {
      dispatch(fetchReservationTemplatesRequest());
      const reservationTemplates = await api.get({
        endpoint: `/reservation-templates`,
      });
      dispatch(fetchReservationTemplatesForOthersSuccess(reservationTemplates));
    } catch (e) {
      dispatch(fetchReservationTemplatesFailure());
      return console.error(e);
    }
  }
);

export const fetchAdvancedSettingsForReservationTemplate = createAppAsyncThunk<
  void,
  number
>(
  'reservationTemplates/fetchAdvancedSettings',
  async (templateId, { dispatch }) => {
    try {
      dispatch(fetchReservationTemplatesRequest());
      const { results = [] } = await api.get({
        endpoint: `/reservation-templates/${templateId}/advanced`,
      });
      dispatch(
        fetchAdvancedSettingsReservationTemplatesSuccess({
          templateId,
          results,
        })
      );
    } catch (e) {
      dispatch(fetchReservationTemplatesFailure());
      return console.error(e);
    }
  }
);

export const createReservationTemplate = createAppAsyncThunk<
  void,
  TReservationTemplate & {
    maxNumberOfObjects?: number;
  }
>(
  'reservationTemplates/createReservationTemplate',
  async (reservationTemplate, { dispatch }) => {
    try {
      dispatch(createReservationTemplateRequest());
      const { id, history, maxNumberOfObjects, ...reservationTemplateBody } =
        reservationTemplate;
      const response = await api.post({
        endpoint: `/reservation-templates`,
        data: reservationTemplateBody,
      });
      dispatch(createReservationTemplateSuccess(response));
      dispatch(fetchReservationTemplates());
    } catch (e: any) {
      dispatch(createReservationTemplateFailure(e?.response?.data?.message));
      return console.error(e?.response?.data ? e?.response?.data : e?.response);
    }
  }
);

export const createUnsavedReservationTemplate =
  (templateType: ETemplateType): TAppThunk =>
  (dispatch) => {
    const response: TReservationTemplate = {
      ...createEmptyReservationTemplate(),
      documentation: '',
      standardOrganization: undefined,
      templateType,
    };
    dispatch(createUnsavedReservationTemplateSuccess(response));
  };

export const setSelectedReservationTemplate =
  (
    isFirstTemplate: boolean,
    reservationTemplate?: TReservationTemplate
  ): TAppThunk =>
  (dispatch) => {
    dispatch(
      setReservationTemplate({
        isFirstTemplate,
        template: reservationTemplate,
      })
    );
  };

export const copyReservationTemplate = createAppAsyncThunk<
  void,
  TReservationTemplate
>(
  'reservationTemplates/copyReservationTemplate',
  async (reservationTemplate, { dispatch }) => {
    const response = {
      ...reservationTemplate,
      name: reservationTemplate.name + '-copy',
      id: 0,
    };
    dispatch(createUnsavedReservationTemplateSuccess(response));
  }
);

export const deleteReservationTemplateItem = createAppAsyncThunk<void, number>(
  'reservationTemplates/deleteReservationTemplateItem',
  async (reservationTemplateId, { dispatch }) => {
    try {
      dispatch(deleteReservationTemplateRequest());
      await api.delete({
        endpoint: `/reservation-templates/${reservationTemplateId}`,
      });
      dispatch(
        deleteRowReservationTemplateSuccess({
          reservationTemplate: { id: reservationTemplateId },
        })
      );
    } catch (e: any) {
      dispatch(deleteReservationTemplateFailure());
      return console.error(e);
    }
  }
);

export const updateReservationTemplate = createAppAsyncThunk<
  void,
  TReservationTemplate & {
    maxNumberOfObjects?: number;
  }
>(
  'reservationTemplates/updateReservationTemplate',
  async (reservationTemplate, { dispatch }) => {
    try {
      dispatch(updateReservationTemplateRequest());
      const {
        id,
        history,
        templateType,
        maxNumberOfObjects,
        ...reservationTemplateBody
      } = reservationTemplate;
      const response = await api.patch({
        endpoint: `/reservation-templates/${reservationTemplate.id}`,
        data: reservationTemplateBody,
      });
      dispatch(updateReservationTemplateSuccess(response));
    } catch (e: any) {
      dispatch(updateReservationTemplateFailure());
      return console.error(e.response.data ? e.response.data : e.response);
    }
  }
);

export const moveReservations = createAppAsyncThunk<
  void,
  {
    reservationTemplate: TReservationTemplate;
    moveReservationsRequest: TMoveReservationsRequest;
  }
>(
  'reservationTemplates/moveReservations',
  async ({ moveReservationsRequest, reservationTemplate }, { dispatch }) => {
    try {
      dispatch(updateReservationTemplateRequest());
      const { ...MoveReservationsRequestBody } = moveReservationsRequest;
      const response = (await api.patch({
        endpoint: `/reservation-templates/${reservationTemplate.id}/move-reservations`,
        data: { ...MoveReservationsRequestBody },
      })) as TMoveReservationsInfo;
      dispatch(moveReservationsSuccess(response));
    } catch (e: any) {
      dispatch(updateReservationTemplateFailure());
      return console.error(e.response.data ? e.response.data : e.response);
    }
  }
);

export const moveReservationsToTrash = createAppAsyncThunk<
  void,
  TReservationTemplate
>(
  'reservationTemplates/moveToTrash',
  async (reservationTemplate, { dispatch }) => {
    try {
      dispatch(updateReservationTemplateRequest());
      const response = await api.patch({
        endpoint: `/reservation-templates/${reservationTemplate.id}/move-to-trash`,
      });
      dispatch(moveToTrashSuccess(response));
    } catch (e: any) {
      dispatch(updateReservationTemplateFailure());
      return console.error(e.response.data ? e.response.data : e.response);
    }
  }
);

export const checkTemplateSaveDisabled =
  (isFirstDrawer: boolean): TAppThunk<boolean> =>
  (_, getState) => {
    const {
      firstSelectedReservationTemplate,
      secondSelectedReservationTemplate,
      map: templateMap,
    } = getState().reservationTemplates;
    if (
      isFirstDrawer &&
      firstSelectedReservationTemplate &&
      templateMap[firstSelectedReservationTemplate.id]
    ) {
      return checkSavedDisabled(
        firstSelectedReservationTemplate,
        templateMap[firstSelectedReservationTemplate.id]
      );
    }
    if (
      !isFirstDrawer &&
      secondSelectedReservationTemplate &&
      templateMap[secondSelectedReservationTemplate.id]
    ) {
      return checkSavedDisabled(
        secondSelectedReservationTemplate,
        templateMap[secondSelectedReservationTemplate.id]
      );
    }
    return true;
  };

export const getFirstDrawer = (): TAppThunk => (_, getState) =>
  getState().reservationTemplates.firstSelectedReservationTemplate;
